import { useState } from "react";
import {
  Activity,
  ActivityType,
  ACTIVITY_TYPES,
  ACTIVITY_TYPE_ORDER,
} from "./plannerTypes";

// Budget breakdown for a trip: sums each priced activity by category and shows
// a simple bar chart (plain CSS, no chart lib) plus total / per-person figures.
export const BudgetPanel = ({
  activities,
  persons,
  currency,
}: {
  activities: Activity[];
  persons: number;
  currency: string;
}) => {
  const [perPerson, setPerPerson] = useState(true);

  const priced = activities.filter((a) => (a.price ?? 0) > 0);
  const total = priced.reduce((s, a) => s + (a.price ?? 0), 0);
  const people = Math.max(1, persons || 1);
  const factor = perPerson ? 1 / people : 1;

  // sum per category, keep only the ones with any spend (ordered by the type list)
  const byCategory = ACTIVITY_TYPE_ORDER.map((type) => ({
    type,
    sum: priced.filter((a) => a.type === type).reduce((s, a) => s + (a.price ?? 0), 0),
  })).filter((c) => c.sum > 0);
  const max = byCategory.reduce((m, c) => Math.max(m, c.sum), 0);

  const cur = currency || "kr";
  const fmt = (v: number) =>
    `${Math.round(v).toLocaleString("sv-SE")} ${cur}`;

  if (priced.length === 0) {
    return (
      <div className="planner__budget">
        <p className="planner__day-hint">
          Lägg till ett pris på flyg och aktiviteter (fältet 💰 Pris i formuläret) så
          summeras budgeten här.
        </p>
      </div>
    );
  }

  return (
    <div className="planner__budget">
      <div className="planner__budget-summary">
        <div className="planner__budget-stat planner__budget-stat--big">
          <span className="planner__budget-num">{fmt(total * factor)}</span>
          <span className="planner__budget-label">{perPerson ? "per person" : "totalt"}</span>
        </div>
        <div className="planner__budget-stat">
          <span className="planner__budget-num">{fmt(perPerson ? total : total / people)}</span>
          <span className="planner__budget-label">{perPerson ? "totalt" : "per person"}</span>
        </div>
        <div className="planner__budget-stat">
          <span className="planner__budget-num">{priced.length}</span>
          <span className="planner__budget-label">poster · {people} pers</span>
        </div>
      </div>

      <div className="planner__budget-toggle">
        <button
          type="button"
          className={`planner__viewtoggle-btn${perPerson ? " planner__viewtoggle-btn--active" : ""}`}
          onClick={() => setPerPerson(true)}
        >
          Per person
        </button>
        <button
          type="button"
          className={`planner__viewtoggle-btn${!perPerson ? " planner__viewtoggle-btn--active" : ""}`}
          onClick={() => setPerPerson(false)}
        >
          Totalt
        </button>
      </div>

      <div className="planner__budget-bars">
        {byCategory.map((c) => {
          const meta = ACTIVITY_TYPES[c.type as ActivityType];
          return (
            <div key={c.type} className="planner__budget-bar-row">
              <span className="planner__budget-bar-label">
                {meta.icon} {meta.label}
              </span>
              <div className="planner__budget-bar-track">
                <div
                  className="planner__budget-bar-fill"
                  style={{ width: `${max > 0 ? (c.sum / max) * 100 : 0}%`, background: meta.color }}
                />
              </div>
              <span className="planner__budget-bar-value">{fmt(c.sum * factor)}</span>
            </div>
          );
        })}
      </div>

      <div className="planner__budget-list">
        {priced
          .slice()
          .sort((a, b) => (b.price ?? 0) - (a.price ?? 0))
          .map((a) => (
            <div key={a.id} className="planner__budget-item">
              <span className="planner__budget-item-icon">{ACTIVITY_TYPES[a.type].icon}</span>
              <span className="planner__budget-item-name">{a.title || ACTIVITY_TYPES[a.type].label}</span>
              <span className="planner__budget-item-price">{fmt((a.price ?? 0) * factor)}</span>
            </div>
          ))}
      </div>
    </div>
  );
};
